/**
 * useActiveOrgId — the org every API call is currently scoped to.
 *
 * Org switching is client-side: OrganizationContext writes `active_cbo_id` to
 * localStorage and dispatches `organizationChanged`; there is no page reload.
 * Anything that fetched org-scoped data therefore keeps rendering the PREVIOUS
 * org's data until something re-runs the fetch.
 *
 * Take this value as a dependency (or a cache-key component) wherever a hook or
 * screen loads org-scoped data, so a switch invalidates and refetches. In prod
 * the Field Config screen kept another org's custom fields on screen after a
 * switch, and saving from there wrote config rows against that org's field ids.
 *
 * Reads the same localStorage key membersAPI puts in the X-CBO-ID header, so it
 * can never disagree with the org the request is actually scoped to.
 */

import { useState, useEffect } from 'react';

export function getActiveOrgId(): string {
  if (typeof window === 'undefined') return 'no-org';
  return localStorage.getItem('active_cbo_id') || 'no-org';
}

export default function useActiveOrgId(): string {
  const [orgId, setOrgId] = useState<string>(getActiveOrgId);

  useEffect(() => {
    const sync = () => setOrgId(getActiveOrgId());
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'active_cbo_id') sync();
    };

    window.addEventListener('organizationChanged', sync);
    window.addEventListener('storage', onStorage);
    // The switch can land between first render and this effect attaching.
    sync();

    return () => {
      window.removeEventListener('organizationChanged', sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return orgId;
}
